/// <amd-module name="Repos/Tfvc/ChangesetsList/Components/ChangesetsHistoryView" />
import * as React from "react";
import { ChangeListSearchCriteria, VersionControlChangeType } from "Repos/Common/Contracts/Git";
import { IChangeset, IChangesetsHistoryViewProps } from "Repos/Common/Contracts/History";
import { getTfvcVersionString } from "Repos/Common/Util/Version";
import * as Resources from "Repos/Tfvc/ChangesetsList/Resources";
import { getTfvcRoutingService } from "Repos/Tfvc/Common/TfvcRoutingService";
import { getTFSProject } from "Tfs/Platform/Page";
import { ObservableArray, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import * as String from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Observer } from "VSSUI/Observer";
import { Filter } from "VSSUI/Utilities/Filter";
import { ZeroData } from "VSSUI/ZeroData";
import { ChangesetsListResult, getChangesetsListDataProviderService } from "../ChangesetsListDataProviderService";
import {
    ChangesetsFilterBar,
    getChangesetsFilterBarStateFromFilterState,
    getInitialChangesetsFilterBarState,
    IChangesetsFilterBarState
} from "./ChangesetsFilterBar";
import { ChangesetsTable } from "./ChangesetsTable";

interface IChangesetsHistoryViewState {
    hasMore: boolean;
}

interface IChangesetsSection {
    changesets: ObservableArray<IChangeset | ObservableValue<IChangeset | undefined>>;
    lastChangesetIdBeforeRename: ObservableValue<number | undefined>;
    loading?: boolean;
    fileName: ObservableValue<string | undefined>;
    prevFileName: ObservableValue<string | undefined>;
    show: ObservableValue<boolean | undefined>;
}

export class ChangesetsHistoryView extends VssComponent<IChangesetsHistoryViewProps, IChangesetsHistoryViewState> {
    private changesetSections = new ObservableArray<IChangesetsSection>([]);
    private filter = new Filter();
    private filterBarState: IChangesetsFilterBarState;
    private firstLoaded = new ObservableValue<boolean>(false);
    private loadHistoryOnMount = false;
    private loadingMore = false;
    private noResultsUrl: string;
    private onLoadedCalled = false;

    constructor(props: IChangesetsHistoryViewProps, context: IVssComponentContext) {
        super(props, context);

        const initialFilterState = props.shouldUpdateUrl ? getInitialChangesetsFilterBarState(context.pageContext) : {};
        this.filter.setState(initialFilterState, true);

        this.filterBarState = getChangesetsFilterBarStateFromFilterState(initialFilterState);

        this.changesetSections.value = [
            {
                changesets: new ObservableArray([]),
                lastChangesetIdBeforeRename: new ObservableValue<number | undefined>(undefined),
                fileName: new ObservableValue<string | undefined>(undefined),
                prevFileName: new ObservableValue<string | undefined>(undefined),
                show: new ObservableValue<boolean>(true)
            }
        ];

        let hasMore = false;
        const changesetsDataService = getChangesetsListDataProviderService(context.pageContext);
        const initialChangesetData = changesetsDataService.getInitialChangesets();
        if (initialChangesetData) {
            this.handleHistoryFetchResult(initialChangesetData, this.changesetSections.value[0]);
            hasMore = initialChangesetData.hasMore;
        } else {
            this.loadHistoryOnMount = true;
        }

        this.noResultsUrl = getAssetLocation(context.pageContext, "ms.vss-code-web/changesets-list/Content/no-results.png");

        this.state = { hasMore };
    }

    public render() {
        return (
            <>
                <ConditionalChildren renderChildren={this.props.filterVisible}>
                    <ChangesetsFilterBar
                        className={this.props.filterClassName}
                        filter={this.filter}
                        onFilterChanged={this.performFilter}
                        shouldUpdateUrl={this.props.shouldUpdateUrl}
                    />
                </ConditionalChildren>
                <Observer changesetSections={this.changesetSections}>
                    {(sectionProps: { changesetSections: IChangesetsSection[] }) => {
                        return sectionProps.changesetSections.map((section, index) => {
                            return (
                                <Observer
                                    changesets={section.changesets}
                                    fileName={section.fileName}
                                    key={index}
                                    lastChangesetIdBeforeRename={section.lastChangesetIdBeforeRename}
                                    show={section.show}
                                >
                                    {(changesetProps: {
                                        changesets: Array<IChangeset | ObservableValue<IChangeset | undefined>>;
                                        fileName: string | undefined;
                                        lastChangesetIdBeforeRename: number | undefined;
                                        show: boolean | undefined;
                                    }) => {
                                        if (index === 0 && changesetProps.changesets.length === 0 && this.firstLoaded.value) {
                                            announce(Resources.NoResultsPrimaryText);
                                            return (
                                                <Card className="flex-center" contentProps={{ contentPadding: false }}>
                                                    <ZeroData
                                                        imagePath={this.noResultsUrl}
                                                        imageAltText={"Image"}
                                                        primaryText={Resources.NoResultsPrimaryText}
                                                        secondaryText={Resources.NoResultsSecondaryText}
                                                    />
                                                </Card>
                                            );
                                        }

                                        const changesetsWithLoading = this.state.hasMore
                                            ? [...changesetProps.changesets, new ObservableValue(undefined)]
                                            : changesetProps.changesets;

                                        const loadMore = () => {
                                            this.loadMoreData(section);
                                        };

                                        const expandButton = (
                                            event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>
                                        ) => {
                                            this.toggleSection(section);
                                            event.preventDefault();
                                        };

                                        const labelForShowMore = !changesetProps.fileName
                                            ? changesetProps.show
                                                ? Resources.HideRenameHistory
                                                : Resources.ShowRenameHistory
                                            : changesetProps.show
                                            ? String.format(Resources.HideRenameHistoryFor, changesetProps.fileName)
                                            : String.format(Resources.ShowRenameHistoryFor, changesetProps.fileName);

                                        return (
                                            <div className="flex-column flex-noshrink" key={`changesets-key-index-${index}`}>
                                                {index !== 0 && changesetProps.lastChangesetIdBeforeRename && (
                                                    <Button text={`${labelForShowMore}`} onClick={expandButton} subtle={true} />
                                                )}
                                                <Card className="bolt-table-card" contentProps={{ contentPadding: false }}>
                                                    {changesetProps.show && (
                                                        <ChangesetsTable
                                                            changesets={changesetsWithLoading}
                                                            dataLoaded={this.firstLoaded}
                                                            loadMore={this.state.hasMore ? loadMore : undefined}
                                                            onActivate={this.props.onActivate}
                                                            showChangeType={this.props.isSingleFileHistory}
                                                            showHeader={this.props.showHeader !== false && index === 0}
                                                        />
                                                    )}
                                                </Card>
                                            </div>
                                        );
                                    }}
                                </Observer>
                            );
                        });
                    }}
                </Observer>
            </>
        );
    }

    public componentDidMount() {
        const filterState = getChangesetsFilterBarStateFromFilterState(this.filter.getState());
        if (this.loadHistoryOnMount) {
            this.performFilter(filterState);
        }

        this.triggerLoaded();
    }

    public componentDidUpdate(prevProps: IChangesetsHistoryViewProps) {
        if (this.props.filePath !== prevProps.filePath) {
            const filterState = getChangesetsFilterBarStateFromFilterState(this.filter.getState());
            this.performFilter(filterState);
        }

        this.triggerLoaded();
    }

    private triggerLoaded() {
        if (this.props.onLoaded && !this.onLoadedCalled && this.firstLoaded.value) {
            this.props.onLoaded();
            this.onLoadedCalled = true;
        }
    }

    private toggleSection = (currentSection: IChangesetsSection) => {
        if (currentSection.show.value !== undefined) {
            currentSection.show.value = !currentSection.show.value;
        } else {
            currentSection.show.value = true;
            this.loadData(currentSection, this.filterBarState, undefined);
        }
    };

    private performFilter = (filterbarState: IChangesetsFilterBarState) => {
        this.filterBarState = filterbarState;

        // Reset the previous history state
        const section: IChangesetsSection = {
            changesets: this.getLoadingRows(),
            lastChangesetIdBeforeRename: new ObservableValue<number | undefined>(undefined),
            loading: true,
            fileName: new ObservableValue<string | undefined>(undefined),
            prevFileName: new ObservableValue<string | undefined>(undefined),
            show: new ObservableValue<boolean>(true)
        };
        this.changesetSections.value = [section];

        this.loadData(section, filterbarState);
    };

    private loadMoreData = (section: IChangesetsSection): void => {
        if (!this.loadingMore) {
            const changesets = section.changesets.value;
            const lastChangeset = ObservableLike.getValue(changesets[changesets.length - 1]);
            if (lastChangeset) {
                this.loadingMore = true;
                this.loadData(section, this.filterBarState, lastChangeset.changesetId);
            }
        }
    };

    private loadData(section: IChangesetsSection, filterbarState?: IChangesetsFilterBarState, skipTo?: number): void {
        const { compareVersion, filePath, version } = this.props;

        const searchCriteria = {
            compareVersion: compareVersion ? getTfvcVersionString(compareVersion) : "",
            itemPath: section.prevFileName.value || filePath || "",
            itemVersion: getTfvcVersionString(version)
        } as ChangeListSearchCriteria;

        if (section.lastChangesetIdBeforeRename.value) {
            searchCriteria.itemVersion = `MR${section.lastChangesetIdBeforeRename.value}`;
        }

        if (filterbarState) {
            searchCriteria.fromDate = filterbarState.dateFrom ? filterbarState.dateFrom.toUTCString() : "";
            searchCriteria.toDate = filterbarState.dateTo ? filterbarState.dateTo.toUTCString() : "";
            searchCriteria.fromVersion = filterbarState.changesetFrom || "";
            searchCriteria.toVersion = filterbarState.changesetTo || "";

            if (filterbarState.owner) {
                searchCriteria.user = filterbarState.owner.localId || "";
            }
        }

        if (skipTo) {
            searchCriteria.toVersion = skipTo.toFixed();
        } else {
            this.firstLoaded.value = false;
            this.onLoadedCalled = false;
        }

        const changesetsDataService = getChangesetsListDataProviderService(this.context.pageContext);
        changesetsDataService.fetchChangesets(searchCriteria).then(results => {
            this.firstLoaded.value = true;
            if (results) {
                this.handleHistoryFetchResult(results, section, skipTo);
                this.setState({ hasMore: results.hasMore });
            } else {
                section.changesets.value = [];
            }
        });
    }

    private handleHistoryFetchResult(results: ChangesetsListResult, section: IChangesetsSection, skipTo?: number) {
        this.firstLoaded.value = true;

        if (results.changesets.length > 0) {
            let lastChangesetIdBeforeRename: number | undefined = undefined;
            const first = results.changesets[0];
            const last = results.changesets[results.changesets.length - 1];

            if ((last.itemChangeType & VersionControlChangeType.Rename) !== 0) {
                lastChangesetIdBeforeRename = last.changeList.changesetId;
            }

            const fileName = first.serverItem;
            section.fileName.value = fileName;

            if (lastChangesetIdBeforeRename) {
                const section: IChangesetsSection = {
                    changesets: this.getLoadingRows(),
                    lastChangesetIdBeforeRename: new ObservableValue<number | undefined>(lastChangesetIdBeforeRename),
                    fileName: new ObservableValue<string | undefined>(undefined),
                    prevFileName: new ObservableValue<string | undefined>(fileName),
                    show: new ObservableValue<boolean | undefined>(undefined)
                };
                this.changesetSections.push(section);
            }

            // Remove the first entry in a follow-rename section since
            // it is the same entry shown in the last line of the previous section.
            // Also on next pages, because we fetch from a known changesetId that is already present.
            if (section.lastChangesetIdBeforeRename.value === first.changeList.changesetId || skipTo) {
                results.changesets = results.changesets.slice(1);
            }
        }

        const routingService = getTfvcRoutingService(this.context.pageContext);
        let newChangesets: Array<IChangeset | ObservableValue<IChangeset | undefined>> = results.changesets.map(entry => {
            const { changeList } = entry;
            const projectName = getTFSProject(this.context.pageContext)!.name;
            const changeset: IChangeset = {
                changesetId: changeList.changesetId,
                changeType: entry.itemChangeType,
                comment: changeList.comment || "",
                commentTruncated: changeList.commentTruncated,
                date: changeList.creationDate,
                linkProps: {
                    href: routingService.getChangesetDetailsUrl(
                        changeList.changesetId,
                        projectName,
                        this.props.isSingleFileHistory ? this.props.filePath : undefined
                    )
                },
                owner: {
                    id: changeList.ownerId,
                    displayName: changeList.ownerDisplayName,
                    imageUrl: changeList.ownerRef?.imageUrl
                } as IdentityRef,
                projectName,
                serverItem: entry.serverItem
            };
            return changeset;
        });

        if (skipTo) {
            newChangesets = section.changesets.value.concat(newChangesets);
        }

        section.changesets.value = newChangesets;

        this.loadingMore = false;
    }

    private getLoadingRows(): ObservableArray<IChangeset | ObservableValue<IChangeset | undefined>> {
        // Use 3 loading rows when loading history
        return new ObservableArray([new ObservableValue(undefined), new ObservableValue(undefined), new ObservableValue(undefined)]);
    }
}

Components.add("repos-changesets-history", ChangesetsHistoryView);
